import React, { useEffect, useRef, useState, useCallback } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import { LoadingButton } from '../../../../components/Buttons/LoadingButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditNoteIcon from '@mui/icons-material/EditNote';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import Close from '@mui/icons-material/Close';
import OpenModalButton from '../../../../components/common/modal/OpenModalButton';
import NewClientCreateSteps from '../../../../components/Proposal/NewClientCreation/NewClientCreateSteps';
import ViewInfo from './ViewInfo';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DeleteClient, GetClients } from '../../../../api/proposals';
import LoadingIndicator from '../../../../components/Global/loading-indicator/loading-indicator';
import { generateArray } from '../../../../components/Utilities/GenerateArray';
import { downloadPdfFile, versionExtract } from '../../../../components/Utilities/HelperMethods';
import ClientCreationWrapper from '../../../../components/HOC/ClientCreationWrapper';
import { CircularProgress } from '@mui/material';
import AlertModal from '../../../../components/common/modal/AlertModal';

const DownloadPdf = ({ handleCreatePDF, isLoadingPdf, isPdfAvailable }) => {
  const handlePdfDownload = useCallback(() => {
    handleCreatePDF?.();
  }, [handleCreatePDF]);
  return (
    <>
      {isLoadingPdf ? (
        <CircularProgress thickness={3} size={20} />
      ) : (
        <Tooltip title="Download">
          <IconButton disabled={!isPdfAvailable} onClick={handlePdfDownload} color="primary">
            <CloudDownloadIcon />
          </IconButton>
        </Tooltip>
      )}
    </>
  );
};

const DownloadPdfWithWrapper = ClientCreationWrapper(DownloadPdf);

export default function ClientTable() {
  const [clients, setClients] = useState([]);
  const [clientID, setClientID] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState();
  const [searchInput, setSearchInput] = useState('');
  const [initialStep, setInitialStep] = useState(1);
  const [selectedClient, setSelectedClient] = useState({});
  const [selectedRow, setSelectTedRow] = useState(undefined);
  const [isClientCreatedSuccess, setIsClientCreatedSuccess] = useState(false);
  // Api
  const deleteClientById = useMutation({
    mutationFn: (clientId) => DeleteClient(Number(clientId))(),
  });
  const clientInfo = useQuery({
    queryKey: ['Clients'],
    queryFn: () => GetClients()(),
    enabled: false,
  });
  const {
    isPending: isClientInfoLoading,
    isError: isClientInfoError,
    isSuccess: isClientInfoSuccess,
  } = clientInfo;

  useEffect(() => {
    fetchClients();
  }, []);
  const fetchClients = (rowIndex) => {
    setSelectTedRow(rowIndex);
    clientInfo.refetch();
  };
  useEffect(() => {
    if (clientInfo.data !== null) {
      const { data } = clientInfo;
      const eachClientVersion = new Map();
      data?.map(
        (client) =>
          client.createdVersions?.length > 0 &&
          eachClientVersion.set(
            client.id,
            client['createdVersions'][client.createdVersions.length - 1].id
          )
      );
      setSelectedVersion(eachClientVersion);
      setClients(clientInfo?.data || []);
      if (selectedRow !== undefined) {
        document.querySelectorAll('.modal-toggle-btn')?.[+selectedRow]?.click();
      }
    }
  }, [clientInfo.data, selectedRow]);

  const handleVersionChange = (value, clientId) => {
    const newVersionMap = new Map(selectedVersion);
    newVersionMap.set(clientId, value);
    setSelectedVersion(newVersionMap);
  };

  const deleteClient = (clientId) => {
    setClientID(clientId);
    deleteClientById.mutate(clientId, {
      onSuccess: () => {
        setClients((prev) => prev.filter((client) => client.id !== Number(clientId)));
        handleToggleDeleteModal();
      },
    });
  };

  const editStepHandler = (client) => {
    if (!client) return;
    const versions = client.createdVersions || [];
    const version = versions.find((version) => version.id === selectedVersion.get(client.id));
    let formatClient = {
      ...client,
      createdVersions:
        versions.length > 0
          ? [{ ...version, illustrationData: versionExtract(version.illustrationData) }]
          : versions,
    };
    setInitialStep(2);
    setIsEditing(true);
    setSelectedClient(formatClient);
  };

  const handleClientSearch = (clients) => {
    return clients.filter(
      (item) =>
        (item.firstName ?? '')
          .concat(' ' + (item.lastName ?? ''))
          .toLowerCase()
          .indexOf(searchInput.toLowerCase().trim()) > -1
    );
  };

  const rowData = handleClientSearch(clients)?.map((cl, index) => ({
    id: cl.id,
    Name: `${cl.firstName} ${cl.lastName}`,
    age: `${cl.age} years`,
    Versions: cl,
    rowIndex: index,
  }));

  const columns = [
    { field: 'Name', headerName: 'Name', width: 300, align: 'left' },
    { field: 'age', headerName: 'Age', width: 300 },
    {
      field: 'Versions',
      headerName: 'Versions Name',
      sortable: false,
      align: 'left',
      width: 400,
      renderCell: ({ row }) =>
        row?.Versions?.createdVersions?.length > 0 ? (
          <FormControl sx={{ width: '80%' }}>
            <Select
              sx={{ border: 'none' }}
              value={selectedVersion.get(row.id)}
              label="Version"
              onChange={(event) => handleVersionChange(event.target.value, row.id)}
              MenuProps={{
                MenuListProps: {
                  sx: {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 1,
                  },
                },
              }}
            >
              {row?.Versions?.createdVersions?.length > 0 &&
                row?.Versions?.createdVersions?.map((version, index) => (
                  <MenuItem key={`${version.versionName}-${index}`} value={version.id}>
                    {version.versionName}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        ) : (
          '-'
        ),
    },
    {
      field: 'Action',
      headerName: 'Action',
      align: 'center',
      description: 'This column has a value getter and is not sortable.',
      sortable: false,
      width: 300,
      renderCell: ({ row }) => (
        <Box>
          <OpenModalButton
            onClose={() => setSelectTedRow(undefined)}
            variant="text"
            isIconButton={true}
            modalSize="xl"
            modalClassName="extra-large"
            modalTitle="Client Information"
            buttonClass="modal-toggle-btn"
            modalBody={
              <ViewInfo
                versionData={versionExtract(
                  row?.Versions?.createdVersions?.find(
                    (version) => version.id === selectedVersion.get(row.id)
                  )?.illustrationData
                )}
                client={clients?.find((client) => client.id === row.id)}
                selectedVersionInfo={row?.Versions?.createdVersions?.find(
                  (version) => version.id === selectedVersion.get(row.id)
                )}
              />
            }
          >
            <Tooltip title="View">
              <VisibilityIcon />
            </Tooltip>
          </OpenModalButton>
          <OpenModalButton
            onOpen={() => editStepHandler(clients?.find((client) => client.id === row.id))}
            useHeader={false}
            modalSize="xl"
            modalTitle="Edit client"
            buttonClass="edit-client-btn"
            isIconButton={true}
            modalBody={
              <NewClientCreateSteps
                rowIndex={row.rowIndex}
                initialClient={selectedClient}
                initialStep={initialStep}
                isEditing={isEditing}
                displayUpdate={fetchClients}
                rowData={row}
                editableClient={clients?.find((client) => client.id === row.id)}
                versionList={row?.Versions?.createdVersions || []}
                oldVersionList={row?.Versions?.createdVersions || []}
                selectedVersionInfo={row?.Versions?.createdVersions?.find(
                  (version) => version.id === selectedVersion.get(row.id)
                )}
              />
            }
          >
            <Tooltip title="Edit">
              <EditNoteIcon />
            </Tooltip>
          </OpenModalButton>

          <DownloadPdfWithWrapper
            shouldDownload={true}
            isPdfAvailable={row?.Versions?.createdVersions?.length > 0}
            versionData={versionExtract(
              row?.Versions?.createdVersions?.find(
                (version) => version.id === selectedVersion.get(row.id)
              )?.illustrationData
            )}
            client={clients?.find((client) => client.id === row.id)}
            selectedVersionInfo={row?.Versions?.createdVersions?.find(
              (version) => version.id === selectedVersion.get(row.id)
            )}
          />

          <Tooltip title="Delete">
            <IconButton color="error" onClick={() => { setClientID(row.id); handleToggleDeleteModal(); }}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
  const handleToggleDeleteModal = () => {
    setDeleteModalIsOpen((prev) => !prev);
  };

  // pagination function
  const pageOptionData = generateArray(clients.length, 9);

  return (
    <div className="client-table">
      {/*Modal*/}
      <AlertModal
        open={isClientCreatedSuccess}
        close={() => setIsClientCreatedSuccess(false)}
        message="Client creation successful."
      />
      {/*delete modal code*/}
      {/* <div className="modal fade" id="deleteModal" tabIndex="-1" role="dialog">
                <div className="modal-dialog" role="document">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title" id="deleteModalLabel">Delete client</h5>
                            <button type="button" className="close" data-dismiss="modal">
                                <span>&times;</span>
                            </button>
                        </div>
                        <div className="modal-body">
                            Are you sure to delete the client?
                        </div>
                        <div className="modal-footer">
                            <button type="button" className="btn btn-danger" data-dismiss="modal">Close</button>
                            <button type="button" className="btn btn-primary" onClick={() => deleteClient(clientID)} data-dismiss="modal">Delete</button>
                        </div>
                    </div>
                </div>
            </div> */}
      {/**/}

      <Dialog open={deleteModalIsOpen} onClose={handleToggleDeleteModal}>
        <DialogTitle>Delete Client</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to delete the client?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleToggleDeleteModal}>
            Cancel
          </Button>
          <LoadingButton
            variant="outlined"
            color="error"
            onClick={() => deleteClient(clientID)}
            loading={deleteClientById.isPending}
            text="Delete"
          />
        </DialogActions>
      </Dialog>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <TextField
          id="input-with-icon-textfield"
          label="Search client"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
          sx={{ marginBottom: '12px', width: '400px', fontSize: '20px' }}
        />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
          <OpenModalButton
            onOpen={() => setInitialStep(1)}
            useHeader={false}
            modalSize="xl"
            modalTitle="New Client"
            isIconButton={false}
            variant={'outlined'}
            modalBody={
              <NewClientCreateSteps
                initialStep={initialStep}
                displayUpdate={fetchClients}
                setIsClientCreatedSuccess={setIsClientCreatedSuccess}
              />
            }
          >
            New Client
          </OpenModalButton>
        </Box>
      </Box>
      <div className="text-center" style={{ height: 600, width: '100%', textAlign: 'center' }}>
        {isClientInfoLoading ? (
          <LoadingIndicator />
        ) : (
          <DataGrid
            sx={{ textAlign: 'center' }}
            disableRowSelectionOnClick={true}
            disableCellSelection
            rows={rowData}
            columns={columns}
            disableColumnMenu
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            pageSizeOptions={pageOptionData}
          />
        )}
      </div>
    </div>
  );
}
